import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Col, Row, Table } from 'antd'

import { defaultFilter, pagedColumnDefaultValues } from 'core/application/commons/defaultOrderableValues'
import OrderedPageFilter from 'core/application/commons/orderedPageFilter'
import deactivateInternalUser from 'core/application/csiDashboard/internalUsers/deactivateUser'
import getInternalUserForSave, {
  InternalUserFoSave,
} from 'core/application/csiDashboard/internalUsers/getInternalUserForSave'
import getInternalUsers, { PagedInternalUsers } from 'core/application/csiDashboard/internalUsers/getInternalUsers'
import reinviteInternalUser from 'core/application/csiDashboard/internalUsers/resendInvitation'
import saveInternalUser, { SaveInternalUserRequest } from 'core/application/csiDashboard/internalUsers/saveInternalUser'

import Card from 'common/components/dataDisplay/Card'
import RowWithAction from 'common/components/dataDisplay/RowWithAction'
import Modal from 'common/components/feedback/Modal'
import notification from 'common/components/feedback/Notification'

import { commandRequest, queryRequest } from 'common/RequestUtils'

import InternalUsersForm from './internalUsersForm/InternalUsersForm'
import InternalUsersTable from './internalUsersTable/InternalUsersTable'

export const INTERNAL_USERS_PATH = 'internal-users'

const InternalUsers: React.FC = () => {
  const { t: translate } = useTranslation()
  const history = useHistory()
  const [loadingInternalUsers, setLoadingInternalUsers] = useState<boolean>(true)
  const [internalUsers, setInternalUsers] = useState<PagedInternalUsers>(pagedColumnDefaultValues)
  const [internalUsersFilter, setInternalUsersFilter] = useState<OrderedPageFilter>(defaultFilter)
  const [showForm, setShowForm] = useState<boolean>(false)
  const [internalUser, setInternalUser] = useState<InternalUserFoSave | null>(null)
  const [submitting, setSubmitting] = useState<boolean>(false)

  useEffect(() => {
    fetchInternalUsers()
  }, [internalUsersFilter])

  const fetchInternalUsers = async () => {
    setLoadingInternalUsers(true)
    const result = await queryRequest(() => getInternalUsers(internalUsersFilter))
    setInternalUsers(result)
    setLoadingInternalUsers(false)
  }

  const onAddInternalUserClick = async () => {
    await displayForm('')
  }

  const displayForm = async (id: string) => {
    const item = await queryRequest(() => getInternalUserForSave(id))
    setInternalUser(item)
    setShowForm(true)
  }

  const submitForm = async (values: SaveInternalUserRequest) => {
    setSubmitting(true)
    const result = await commandRequest(() => saveInternalUser(values))
    if (!result.errors) {
      setShowForm(false)
      notification.open({
        message: 'Internal user saved',
        type: 'success',
      })
      setInternalUser(null)
      await fetchInternalUsers()
    } else {
      notification.open({
        message: result.errors[0],
        type: 'error',
      })
    }
    setSubmitting(false)
  }

  const onReinvite = (id: string, fullName: string) => {
    Modal.confirm({
      title: translate('csi-dashboard.internal-users.confirm-reinvite-title').replace('[]', fullName),
      content: translate('csi-dashboard.internal-users.confirm-reinvite-message'),
      async onOk() {
        const res = await commandRequest(() => reinviteInternalUser(id))
        if (!res.errors) {
          notification.open({
            message: translate('csi-dashboard.internal-users.reinvite-success'),
            type: 'success',
          })
          await fetchInternalUsers()
        } else {
          notification.open({
            message: res.errors[0],
            type: 'error',
          })
        }
      },

      okText: translate('general.send'),
      okType: 'primary',
      cancelText: translate('general.cancel'),
    })
  }

  const onDeactivate = (id: string, fullName: string) => {
    Modal.confirm({
      title: translate('csi-dashboard.internal-users.confirm-deactivate-user-title').replace('[]', fullName),
      content: translate('csi-dashboard.internal-users.confirm-deactivate-user-message'),
      async onOk() {
        const res = await commandRequest(() => deactivateInternalUser(id))
        if (!res.errors) {
          notification.open({
            message: translate('csi-dashboard.internal-users.deactivate-user-success'),
            type: 'success',
          })
          await fetchInternalUsers()
        } else {
          notification.open({
            message: res.errors[0],
            type: 'error',
          })
        }
      },

      okText: translate('general.send'),
      okType: 'primary',
      cancelText: translate('general.cancel'),
    })
  }

  return (
    <>
      <Row className="mt-24">
        <Col span={24}>
          <Card title={translate('csi-dashboard.internal-users.title')}>
            <InternalUsersTable
              searchFilter={internalUsersFilter}
              setSearchFilter={setInternalUsersFilter}
              internalUsers={internalUsers}
              loadingInternalUsers={loadingInternalUsers}
              history={history}
              translate={translate}
              onEdit={async (id) => {
                await displayForm(id)
              }}
              onDeactivate={onDeactivate}
              onReinvite={onReinvite}
            />
          </Card>
        </Col>
      </Row>

      {internalUser && (
        <InternalUsersForm
          initialValues={internalUser}
          visible={showForm}
          onCancel={() => {
            setInternalUser(null)
            setShowForm(false)
          }}
          onSubmit={submitForm}
          submitting={submitting}
        />
      )}
    </>
  )
}

export default InternalUsers
